import React, { useState, useMemo, useEffect } from "react";
import { X, Loader, ImageOff } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setactiveVariant } from "src/store/slices/activeVariant-slice";
import { setactiveSlot } from "src/store/slices/activeSlot-slice";
import ProgressBar from "src/components/ui-elements/ProgressBar";
import { handleCheckoutClicked } from "src/helpers/checkoutHelper";
import { store } from "src/store/store";
import {
  asyncDeleteCartItem,
  decreaseQuantityAsync,
  increaseQuantityAsync,
} from "src/store/slices/cartThunks";
import { getPrimaryButtonColors } from "src/config/buttonColorConfig";
import { getImageUrl, firstImagePath, isVideoPath } from "src/utils/imageUrl";

const CartDrawer = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency.currencyName);
  const globalData = useSelector((state) => state.globalData.globalDataName);
  const cartItems = useSelector((state) => state.cart.cartItems);
  const accessToken = useSelector((state) => state.accessToken.token);
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const primaryColors = getPrimaryButtonColors();

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const grandTotal = subtotal;

  // Ensure primaryColors has valid string values
  const safeButtonColors = {
    bg: primaryColors?.bg || '#050505',
    text: primaryColors?.text || '#fdfcfc',
    hoverBg: primaryColors?.hoverBg || '#fafafa',
    hoverText: primaryColors?.hoverText || '#333333',
  };

  const shippingType = globalData?.shippingType;
  const shippingFreeAbove = globalData?.shippingFreeAbove || 0;

  const showProgressBar = shippingType === 2 && shippingFreeAbove > 0;

  const progress = useMemo(() => {
    if (!showProgressBar) return 0;
    const remainingAmount = shippingFreeAbove - grandTotal;
    return remainingAmount > 0
      ? ((shippingFreeAbove - remainingAmount) / shippingFreeAbove) * 100
      : 100;
  }, [grandTotal, shippingFreeAbove, showProgressBar]);

  const remainingForFreeShipping =
    shippingFreeAbove - grandTotal > 0
      ? (shippingFreeAbove - grandTotal).toFixed(2)
      : 0;

  useEffect(() => {
    console.log(cartItems);
  }, [cartItems]);

  return (
    <>
      <style jsx>{`
        .view-cart-btn {
          position: relative;
          overflow: hidden;
          transition: color 0.3s ease;
        }

        .view-cart-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background-color: black;
          transform: translateX(-100%);
          transition: transform 0.3s ease-out;
          z-index: 1;
        }

        .view-cart-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          width: 150%;
          left: -25%;
          transform: translateX(-100%);
          transition: transform 0.7s ease-out;
          z-index: 2;
        }

        .view-cart-btn:hover::before {
          transform: translateX(0);
        }

        .view-cart-btn:hover::after {
          transform: translateX(100%);
        }

        .view-cart-btn:hover {
          color: white;
        }

        .view-cart-btn .btn-text {
          position: relative;
          z-index: 10;
        }

        .checkout-btn {
          position: relative;
          overflow: hidden;
        }

        .checkout-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          width: 150%;
          left: -25%;
          transform: translateX(-100%);
          transition: transform 0.7s ease-out;
          z-index: 2;
        }

        .checkout-btn:hover::after {
          transform: translateX(100%);
        }

        .checkout-btn .btn-text {
          position: relative;
          z-index: 10;
        }

        /* For better touch device support */
        @media (hover: none) and (pointer: coarse) {
          .view-cart-btn::before,
          .view-cart-btn::after,
          .checkout-btn::after {
            display: none;
          }
        }
      `}</style>

      <div
        className={`fixed inset-0 bg-black bg-opacity-50 z-[1040] transition-opacity ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={onClose}
      ></div>

      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[450px] bg-white shadow-lg z-[1040] transform font-outfit ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 ease-in-out flex flex-col`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-[20px] md:text-[24px] lg:text-2xl font-outfitSemiBold flex items-center justify-center">
            Your Cart{" "}
            <span className="ms-2 rounded-full grid place-items-center w-8 h-8 bg-black text-white text-base">
              {cartItems.length}
            </span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
          >
            <X
              size={24}
              className="transition-transform duration-300 hover:rotate-90"
            />
          </button>
        </div>

        <div className="flex-grow flex flex-col overflow-hidden">
          {showProgressBar && cartItems.length > 0 && (
            <div className="px-4 py-3 border-b">
              {progress === 100 ? (
                <h1 className="mb-2 font-outfitRegular">
                  You are eligible for free shipping.
                </h1>
              ) : (
                <h1 className="mb-2 font-outfitLight">
                  Spend{" "}
                  <span className="font-outfitBold">{`${currency}${remainingForFreeShipping}`}</span>{" "}
                  more to reach free shipping!
                </h1>
              )}
              <ProgressBar progress={progress} />
            </div>
          )}

          <div className="flex-grow overflow-y-auto px-4 py-4 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-white [&::-webkit-scrollbar-thumb]:bg-gray-400">
            {cartItems.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <h1 className="text-2xl font-outfitRegular">
                  Your cart is empty
                </h1>
              </div>
            ) : (
              cartItems.map((item, index) => (
                <CartItem
                  key={`${item.ID || item.productId}-${item.variantId || 'no-variant'}-${item.slotId || 'no-slot'}-${index}`}
                  item={item}
                  currency={currency}
                  onClose={onClose}
                  navigate={navigate}
                  accessToken={accessToken}
                />
              ))
            )}
          </div>
        </div>

        {cartItems.length > 0 && (
          <div className="border-t px-4 py-4 bg-white">
            <div className="flex justify-between items-center text-lg font-semibold mb-4">
              <h1 className="text-xl font-outfitRegular text-gray-800">
                Estimated Total
              </h1>
              <h1 className="text-2xl font-outfitRegular text-gray-800">{`${currency}${grandTotal}`}</h1>
            </div>
            <div className="flex gap-x-3">
              {/* View Cart Button with CSS Animation */}
              <button
                className={`btn-secondary px-7 py-3 h-12 transition rounded-full text-[16px] font-outfitSemiBold Capitalize whitespace-nowrap ${
                  processing
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                onClick={() => {
                  if (!processing) {
                    onClose();
                    navigate("/cart");
                  }
                }}
                disabled={processing}
              >
                <span className="btn-text">View Cart</span>
              </button>

              {/* Checkout Button */}
              {processing ? (
                <button
                  disabled
                  className="flex-1 px-4 py-3 h-12 transition rounded-full text-[16px] font-bold flex items-center justify-center whitespace-nowrap"
                  style={{
                    backgroundColor: safeButtonColors.bg,
                    color: safeButtonColors.text,
                    border: `1px solid ${safeButtonColors.bg}`,
                    fontFamily: 'var(--font-heading)',
                    opacity: 0.6,
                    cursor: 'not-allowed',
                  }}
                >
                  <Loader size={16} className="mr-2 animate-spin" />
                  Processing...
                </button>
              ) : (
                <button
                  className="checkout-btn flex-1 px-4 py-3 h-12 transition rounded-full text-[16px] font-bold Capitalize whitespace-nowrap"
                  style={{
                    backgroundColor: safeButtonColors.bg,
                    color: safeButtonColors.text,
                    border: `1px solid ${safeButtonColors.bg}`,
                    fontFamily: 'var(--font-heading)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = safeButtonColors.hoverBg;
                    e.currentTarget.style.color = safeButtonColors.hoverText;
                    e.currentTarget.style.borderColor = safeButtonColors.hoverBg;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = safeButtonColors.bg;
                    e.currentTarget.style.color = safeButtonColors.text;
                    e.currentTarget.style.borderColor = safeButtonColors.bg;
                  }}
                  onClick={() => {
                    handleCheckoutClicked(
                      dispatch,
                      setProcessing,
                      onClose,
                      navigate,
                      cartItems,
                      accessToken
                    );
                  }}
                >
                  <span className="btn-text">Checkout</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const CartItem = ({ item, currency, onClose, navigate, accessToken }) => {
  const dispatch = useDispatch();
  const imageUrl = useMemo(() => {
    let url = "";
    if (item?.images) {
      url = getImageUrl(firstImagePath(item.images));
      if (item?.variantId) {
        for (let i = 0; i < item.images.length; i++) {
          if (
            item.images[i].variant_id === item.variantId &&
            !isVideoPath(item.images[i]?.imagePath)
          ) {
            url = getImageUrl(item.images[i]?.imagePath);
            break;
          }
        }
      }
    }

    if (item?.image) url = getImageUrl(item?.image);

    return url;
  }, [item]);

  const handleNavigate = () => {
    if (item.variantId) {
      dispatch(setactiveVariant(item.variantId));
    }
    if (item.slotId) {
      dispatch(setactiveSlot(item.slotId));
    }
    onClose();
    navigate(`/product/${item.slug}`);
  };

  return (
    <div className="flex sm:flex-row mb-4 sm:mb-8 gap-3">
      <div
        className="flex flex-row items-start justify-left sm:w-auto cursor-pointer"
        onClick={handleNavigate}
      >
        <div className="relative w-20 h-20 sm:w-24 sm:h-24 overflow-hidden rounded">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          
          {/* Fallback placeholder */}
          <div 
            className={`${imageUrl ? 'hidden' : 'flex'} w-full h-full bg-gray-200 rounded cursor-pointer items-center justify-center flex-col text-gray-500`}
            style={{ display: imageUrl ? 'none' : 'flex' }}
          >
            <ImageOff size={24} className="mb-1 opacity-50" />
            <span className="text-xs font-outfitMedium">No Image</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col text-left gap-1 flex-1">
        <h4
          className="font-outfitSemiBold text-lg text-gray-800 cursor-pointer"
          onClick={handleNavigate}
        >
          {item.name}
        </h4>
        {(item.variantName || (item?.productType === 2 && item.variantName)) && (
          <p className="font-outfitSemiBold text-base text-gray-500">
            {item.variantName}
          </p>
        )}
        <p className="font-outfitSemiBold text-base sm:text-lg text-gray-800">{`${currency} ${item.price}`}</p>
      </div>
      <div className="flex items-center flex-col gap-4 sm:gap-8 sm:w-auto">
        <button
          onClick={() =>
            store.dispatch(
              asyncDeleteCartItem(item, item.cartItemId, accessToken)
            )
          }
          className="ml-auto"
        >
          <X size={24} className="size-4 stroke-gray-500" />
        </button>
        <div className="border border-gray-200 rounded-full flex items-center px-2">
          <button
            onClick={() => {
              // Don't allow quantity changes for digital products
              if (item?.productType === 1) return;
              store.dispatch(
                decreaseQuantityAsync(
                  item,
                  item.quantity,
                  item?.variantId,
                  accessToken,
                  item?.slotId
                )
              );
            }}
            className="font-outfitRegular rounded text-gray-800 text-xl"
          >
            -
          </button>
          <input
            type="number"
            value={item.quantity}
            readOnly
            min="1"
            className="w-14 p-2 text-center text-base sm:text-md appearance-none font-outfitRegular text-gray-800"
          />
          <button
            onClick={() => {
              // Don't allow quantity changes for digital products
              if (item?.productType === 1) return;
              store.dispatch(
                increaseQuantityAsync(
                  item,
                  item.quantity,
                  item?.variantId,
                  accessToken,
                  item?.slotId
                )
              );
            }}
            className="font-outfitRegular text-gray-800 rounded text-xl"
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
